import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from '../../Redux/CustomHooks';
import {
  fetchDiningTables,
  addDiningTable,
  deleteDiningTable,
  updateSortIndex,
} from '../../Redux/DiningTable/diningTableApi';
import { RootState } from '../../Redux/Store';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Header from '../../Components/Header/Header';
import { DiningTable } from '../../Redux/DiningTable/diningTableSlice';
import styled from '@emotion/styled';
import {
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Alert,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PrintIcon from '@mui/icons-material/Print';
import { useDraggable } from '../../hooks/useDraggable';
import QRCodeModal from '../../Components/Dialog/QRCodeModal';
import BulkQRCodePrinter from '../../Components/DiningTable/BulkQRCodePrinter';

const TableItem = styled.div<{ isDragging: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: ${props => props.isDragging ? '#f0f0f0' : 'white'};
  border-bottom: 1px solid #ddd;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DragHandle = styled.div`
  cursor: grab;
  display: flex;
  align-items: center;
  margin-right: 16px;
  color: rgba(0, 0, 0, 0.54);
`;

interface TableRowProps {
  table: DiningTable;
  onDelete: (id: string) => void;
  onShowQr: (table: DiningTable) => void;
}

const TableRow: React.FC<TableRowProps> = ({ table, onDelete, onShowQr }) => {
  const { isDragging, setNodeRef, setActivatorNodeRef, style, attributes, listeners } = useDraggable(
    table.id,
    { direction: 'vertical' }
  );

  return (
    <TableItem
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
    >
      <DragHandle
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon />
      </DragHandle>
      <Typography sx={{ flexGrow: 1 }}>{table.name}</Typography>
      <IconButton
        onClick={() => onShowQr(table)}
        color="primary"
        size="small"
        aria-label="QRコードを表示"
      >
        <QrCode2Icon />
      </IconButton>
      <IconButton
        edge="end"
        onClick={() => onDelete(table.id)}
        color="error"
        size="small"
      >
        <DeleteIcon />
      </IconButton>
    </TableItem>
  );
};

const DiningTablePage: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const dispatch = useDispatch();
  const diningTables = useSelector((state: RootState) => state.diningTable.tables);
  const [newTableName, setNewTableName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [qrTable, setQrTable] = useState<DiningTable | null>(null);
  const [bulkPrinting, setBulkPrinting] = useState(false);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!store_id) {
      setError('Store ID is missing');
      return;
    }
    dispatch(fetchDiningTables(store_id)).catch((err) => setError(err.message));
  }, [dispatch, store_id]);

  const handleAddTable = () => {
    if (!newTableName || !store_id) return;

    dispatch(addDiningTable({ storeId: store_id, name: newTableName }))
      .unwrap()
      .then(() => setNewTableName(''))
      .catch(() => setError('Failed to add dining table.'));
  };

  const handleDelete = (id: string) => {
    if (!store_id) return;

    dispatch(deleteDiningTable({ id, storeId: store_id }))
      .unwrap()
      .catch(() => setError('Failed to delete dining table.'));
  };

  const handlePrintAllQr = () => {
    if (diningTables.length === 0) return;
    setBulkPrinting(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !store_id) return;

    if (active.id !== over.id) {
      const oldIndex = diningTables.findIndex((table) => table.id === active.id);
      const newIndex = diningTables.findIndex((table) => table.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedTables = arrayMove(diningTables, oldIndex, newIndex);

        const reorderedTables = updatedTables.map((table, index) => ({
          ...table,
          sortIndex: index + 1,
        }));

        dispatch({
          type: 'diningTable/updateLocalOrder',
          payload: reorderedTables,
        });
        setHasChanges(true);
      }
    }
  };

  const handleSaveChanges = async () => {
    if (!store_id || !hasChanges) return;

    try {
      const reorderedTables = diningTables.map((table, index) => ({
        ...table,
        sortIndex: index + 1,
      }));

      await Promise.all(
        reorderedTables.map((table) =>
          dispatch(updateSortIndex({ storeId: store_id, table })).unwrap()
        )
      );
      setHasChanges(false);
    } catch (err) {
      setError('Failed to update table order.');
    }
  };

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          テーブル管理
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              value={newTableName}
              placeholder="テーブル名を入れてください"
              onChange={(e) => setNewTableName(e.target.value)}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleAddTable}
              disabled={!newTableName}
              sx={{ flexShrink: 0 }}
            >
              テーブルを追加
            </Button>
          </Box>
        </Paper>

        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChanges}
            startIcon={<SaveIcon />}
            disabled={!hasChanges}
          >
            並び順を保存
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handlePrintAllQr}
            startIcon={<PrintIcon />}
            disabled={diningTables.length === 0}
          >
            全テーブルのQRを印刷
          </Button>
        </Box>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={diningTables.map((table) => table.id)}
            strategy={verticalListSortingStrategy}
          >
            <Paper>
              {diningTables.map((table) => (
                <TableRow
                  key={table.id}
                  table={table}
                  onDelete={handleDelete}
                  onShowQr={setQrTable}
                />
              ))}
            </Paper>
          </SortableContext>
        </DndContext>
      </Container>

      {qrTable && store_id && (
        <QRCodeModal
          open={Boolean(qrTable)}
          onClose={() => setQrTable(null)}
          storeId={store_id}
          table={qrTable}
        />
      )}

      {store_id && (
        <BulkQRCodePrinter
          storeId={store_id}
          tables={diningTables}
          trigger={bulkPrinting}
          onDone={() => setBulkPrinting(false)}
        />
      )}
    </>
  );
};

export default DiningTablePage;
